import { useCallback, useEffect, useState } from 'react';
import type { DownloadLink, TimelineEntry } from '@gc/api-client';

/**
 * A gadget's whole life in one list: state changes, assignments and documents interleaved in
 * the order they happened. The API does the merging, so a mobile client showing the same
 * timeline cannot end up ordering it differently.
 *
 * Newest first, because someone opening a gadget almost always wants to know what just
 * happened to it, not what happened when it was bought.
 */

export interface TimelineApi {
  getTimeline: (gadgetId: string) => Promise<{ items: TimelineEntry[] }>;
  getDocumentUrl: (documentId: string) => Promise<DownloadLink>;
}

type Load = { kind: 'loading' } | { kind: 'ready'; items: TimelineEntry[] } | { kind: 'error'; message: string };

const STATE_LABELS: Record<string, string> = {
  procured: 'Procured', in_stock: 'In stock', assigned: 'Assigned', in_service: 'In service',
  under_repair: 'Under repair', awaiting_parts: 'Awaiting parts', returned: 'Returned',
  retired: 'Retired', disposed: 'Disposed',
};

const label = (state: string): string => STATE_LABELS[state] ?? state;

export function Timeline({ gadgetId, api }: { gadgetId: string; api: TimelineApi }) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [opening, setOpening] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoad({ kind: 'loading' });
    api.getTimeline(gadgetId)
      .then((r) => live && setLoad({ kind: 'ready', items: r.items }))
      .catch((e: unknown) => live && setLoad({ kind: 'error', message: msg(e) }));
    return () => { live = false; };
  }, [api, gadgetId]);

  /**
   * Documents are fetched on demand rather than pre-signed with the timeline: a URL minted
   * when the page loaded would be expired by the time anyone clicked it, and pre-signing every
   * document on every page view signs far more than anyone opens.
   */
  const open = useCallback(async (documentId: string) => {
    setOpening(documentId);
    setFailure(null);
    try {
      const link = await api.getDocumentUrl(documentId);
      window.open(link.url, '_blank', 'noopener');
    } catch (e) {
      setFailure(`Could not open that document: ${msg(e)}`);
    } finally {
      setOpening(null);
    }
  }, [api]);

  if (load.kind === 'loading') return <p>Loading history…</p>;
  if (load.kind === 'error') return <p data-testid="timeline-error">Could not load history: {load.message}</p>;
  if (load.items.length === 0) return <p data-testid="timeline-empty">Nothing has happened to this gadget yet.</p>;

  return (
    <section>
      <h2>History</h2>
      {failure && <p role="alert" data-testid="timeline-failure">{failure}</p>}

      <ol className="timeline">
        {load.items.map((e, i) => (
          <li key={`${e.kind}-${e.at}-${i}`} data-testid={`entry-${e.kind}`}>
            <time dateTime={e.at}>{when(e.at)}</time>

            {e.kind === 'transition' && (
              <span>
                {e.from ? <>{label(e.from)} → <strong>{label(e.to)}</strong></> : <strong>{label(e.to)}</strong>}
                {' — '}{e.reason}
              </span>
            )}

            {e.kind === 'assignment' && (
              <span>
                Assigned to <strong>{e.ownerTeam ?? 'a person'}</strong>
                {e.location ? <> at {e.location}</> : null}
              </span>
            )}

            {e.kind === 'document' && (
              <span>
                {e.documentKind} added: <strong>{e.filename}</strong>{' '}
                <button
                  type="button"
                  onClick={() => void open(e.documentId)}
                  disabled={opening === e.documentId}
                  aria-label={`Open ${e.filename}`}
                >
                  {opening === e.documentId ? 'Opening…' : 'Open'}
                </button>
              </span>
            )}

            {e.kind === 'repair' && (
              <span>
                Repair <code>{e.shortId}</code>{' '}
                {e.from ? <>{repairLabel(e.from)} → </> : null}<strong>{repairLabel(e.to)}</strong>
                {' — '}{e.from ? e.reason : e.fault}
                {e.to === 'completed' && e.costMinor !== null
                  ? <> · ₹{(e.costMinor / 100).toLocaleString('en-IN')}</>
                  : null}
              </span>
            )}

            <span className="actor"> · {e.actor}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

const msg = (e: unknown): string => (e instanceof Error ? e.message : 'unknown error');

const REPAIR_LABELS: Record<string, string> = {
  raised: 'Raised', triaged: 'Triaged', dispatched: 'Dispatched', in_repair: 'In repair',
  awaiting_parts: 'Awaiting parts', completed: 'Completed', cancelled: 'Cancelled',
};

const repairLabel = (state: string): string => REPAIR_LABELS[state] ?? state;

function when(iso: string): string {
  const d = new Date(iso.replace(' ', 'T'));
  return isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
